import type {
  GbpLocalPost,
  GbpLocalPostCtaType,
  GbpLocalPostSource,
  GbpPhotoCategory,
  GbpPhotoSource,
  GbpPhotoSubject,
} from '@/api/generated/schemas';
import type { BusinessKindCopy } from '../use-business-kind-copy';

// Labels and chips for the Posts & photos tab. Chips are calm on purpose: waiting / refused by Google = amber
// soft, approved / live = success soft — never red, nothing here is an error the owner caused.

export type PostChipTone = 'amber' | 'success' | 'neutral';

export const POST_CHIP_CLASS: Record<PostChipTone, string> = {
  amber: 'bg-[var(--butter-50)] text-[var(--butter-700)]',
  success: 'bg-[var(--c-success-soft)] text-[var(--c-success-soft-fg)]',
  neutral: 'bg-clovie-surface-2 text-clovie-text-2',
};

/** Status chip of a post: Google's own state wins once the post reached Google. */
export function postStatusChip(post: Pick<GbpLocalPost, 'status' | 'googleState'>): { label: string; tone: PostChipTone } {
  switch (post.status) {
    // Only drafts from before posts went out without approval are still in this state.
    case 'NEEDS_APPROVAL':
      return { label: 'Not scheduled', tone: 'amber' };
    case 'SCHEDULED':
      return { label: 'Scheduled', tone: 'success' };
    case 'PUBLISHING':
      return { label: 'Publishing', tone: 'neutral' };
    case 'LIVE':
      return post.googleState === 'PROCESSING' ? { label: 'In Google review', tone: 'neutral' } : { label: 'Live', tone: 'success' };
    case 'REJECTED':
      return { label: 'Google said no', tone: 'amber' };
    case 'FAILED':
      return { label: 'Didn’t go out', tone: 'amber' };
    case 'CANCELLED':
      return { label: 'Not posted', tone: 'neutral' };
  }
}

export const POST_SOURCE_LABEL: Record<GbpLocalPostSource, string> = {
  CAMPAIGN: 'your campaign',
  PHOTOS: 'your new photos',
  SERVICE_SPOTLIGHT: 'your services',
  MANUAL: 'you',
};

export const POST_CTA_LABEL: Record<NonNullable<GbpLocalPostCtaType>, string> = {
  BOOK: 'Book',
  LEARN_MORE: 'Learn more',
  CALL: 'Call',
};

export function postCtaLabel(ctaType: GbpLocalPostCtaType): string {
  return ctaType ? POST_CTA_LABEL[ctaType] : 'No button';
}

/** "Inside the salon", "Outside the restaurant"; a generic business just "Inside" / "Outside". */
const placeLabel = (side: 'Inside' | 'Outside', kind: BusinessKindCopy) => (kind.kind === 'OTHER' ? side : `${side} the ${kind.noun}`);

const AT_WORK_CATEGORY_LABEL: Record<BusinessKindCopy['kind'], string> = {
  NAIL: 'Nails & work',
  HAIR: 'Hair & work',
  RESTAURANT: 'At work',
  OTHER: 'At work',
};

const PHOTO_CATEGORY_LABEL: Record<Exclude<GbpPhotoCategory, 'AT_WORK' | 'INTERIOR' | 'EXTERIOR'>, string> = {
  TEAMS: 'Team',
  FOOD_AND_DRINK: 'Food & drinks',
  ADDITIONAL: 'Other',
};

/** The Google category a photo goes under, worded for the kind of business. */
export function photoCategoryLabel(category: GbpPhotoCategory, kind: BusinessKindCopy): string {
  switch (category) {
    case 'AT_WORK':
      return AT_WORK_CATEGORY_LABEL[kind.kind];
    case 'INTERIOR':
      return placeLabel('Inside', kind);
    case 'EXTERIOR':
      return placeLabel('Outside', kind);
    default:
      return PHOTO_CATEGORY_LABEL[category] ?? PHOTO_CATEGORY_LABEL.ADDITIONAL;
  }
}

export const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

/** "10 AM", "12 PM", "7 PM" — salon-local hour 0–23. */
export function formatHourLabel(hour: number): string {
  const suffix = hour < 12 ? 'AM' : 'PM';
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve} ${suffix}`;
}

const PHOTO_SUBJECT_LABEL: Record<Exclude<NonNullable<GbpPhotoSubject>, 'INTERIOR' | 'EXTERIOR'>, string> = {
  NAILS: 'Nails',
  HAIR: 'Hair',
  FOOD: 'Food & drinks',
  WORK: 'Our work',
  AT_WORK: 'At work',
  TEAM: 'Team',
};

/** What a library photo shows (the photo check decides) — chip on Up next and the library. */
export function photoSubjectChipLabel(subject: NonNullable<GbpPhotoSubject>, kind: BusinessKindCopy): string {
  if (subject === 'INTERIOR') return placeLabel('Inside', kind);
  if (subject === 'EXTERIOR') return placeLabel('Outside', kind);
  return PHOTO_SUBJECT_LABEL[subject] ?? 'Photo';
}

/** Where a library photo came from — the chip on its thumbnail. */
export const PHOTO_SOURCE_LABEL: Record<GbpPhotoSource, string> = { INSTAGRAM: 'Instagram', UPLOAD: 'Upload', BIO_GALLERY: 'Bio page' };

/** "Oct 8" in the salon's timezone (browser clock for a moment while the timezone loads — display only). */
export function formatSalonMonthDay(iso: string, timezone: string | undefined): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: timezone, month: 'short', day: 'numeric' }).format(new Date(iso));
}

/** "Tue, Oct 13" in the salon's timezone. */
export function formatSalonDay(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(iso));
}

/** "10 AM", "9:30 AM" in the salon's timezone. */
export function formatSalonTime(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: timezone, hour: 'numeric', minute: '2-digit', hourCycle: 'h12' })
    .format(new Date(iso))
    .replace(':00', '');
}

/** "Tue, Oct 13 · 10 AM" in the salon's timezone (posts go out at salon time). */
export function formatPostWhen(iso: string, timezone: string): string {
  return `${formatSalonDay(iso, timezone)} · ${formatSalonTime(iso, timezone)}`;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days since `iso` (0 = today / in the future). */
export function daysSince(iso: string, now: number): number {
  return Math.max(0, Math.floor((now - new Date(iso).getTime()) / DAY_MS));
}

export function formatDaysAgo(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

/** "Oct 31" from a calendar day ("YYYY-MM-DD") — no timezone shift. */
export function formatIsoDay(isoDay: string): string {
  const [year, month, day] = isoDay.split('-').map(Number);
  if (!year || !month || !day) return isoDay;
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' }).format(new Date(Date.UTC(year, month - 1, day)));
}
